import type {
  CustomFilterOption,
  CustomFilters,
  Filter,
  FilterPrice,
  FilterResponse,
} from "~/types/Data/product/filter";
import type {
  FilterProps,
  BaseFilterProps,
  OptionsFiltersMap,
  PriceFiltersSchema,
} from "~/types/schemas/product/Organism/filters";
import type { Category } from "~/types/Data/product/Categories";
import paths from "~/constants/paths";
import { specialTagsFilter, rateFilter } from "~/constants/product/filters";
import useCategories from "~/composables/product/useCategories";
import useBrands from "~/composables/product/useBrands";

const MAX_NUM_OPTION = 3;

export default function useFilters(loading?: Ref<boolean>) {
  const { categoriesMap, subcategoriesMap, collectionsMap } = useCategories();
  const { filteredBrands } = useBrands();

  const route = useRoute();
  const router = useRouter();
  const { t } = useI18n();
  const nuxtApp = useNuxtApp();

  const optionsFilter = useState<OptionsFiltersMap | undefined>(
    "options-filter",
    () => undefined,
  );

  const priceFilterBoundaries = useState<FilterPrice | undefined>(
    "price-filter-boundaries",
    () => undefined,
  );

  const customFilters = useState<
    Record<CustomFilters, BaseFilterProps[] | undefined> | undefined
  >("custom-filters", () => undefined);

  const priceFilter = computed<PriceFiltersSchema | undefined>(() => {
    if (route.query.price) {
      return {
        q_key: "price",
        type: "PRICE",
        value: route.query.price.toString(),
        min_price: Number(JSON.parse(route.query.price.toString()).min_price),
        max_price: Number(JSON.parse(route.query.price.toString()).max_price),
        currency: nuxtApp.$currency,
      };
    }
  });

  const appliedFilters = computed(() => {
    const allFilters = Object.values({
      ...displayedCategories.value,
      ...customFilters.value,
      ...optionsFilter.value,
      specialTagsFilter,
      rateFilter,
    });

    const activeFilters = allFilters.reduce<FilterProps[]>(
      (acc, filterValues) => {
        if (!filterValues) return acc;

        return [
          ...acc,
          ...filterValues.reduce<FilterProps[]>((filtered, item) => {
            if (isApplied(item.q_key, item.value)) {
              // Process the label in the same loop that filters
              const processedItem =
                item.label && item.label.includes(".")
                  ? { ...item, label: t(item.label) }
                  : item;

              filtered.push(processedItem);
            }
            return filtered;
          }, []),
        ];
      },
      [],
    );

    if (priceFilter.value) {
      activeFilters.push(priceFilter.value);
    }

    return activeFilters;
  });

  // This limits the selected options to three at a time
  const displayedOptionsFilter = computed(() => {
    const newOptionFilters = appliedFilters.value.reduce<OptionsFiltersMap>(
      (acc, filter) => {
        if (filter.type === "OPTION" && optionsFilter.value?.[filter.q_key]) {
          acc[filter.q_key] = optionsFilter.value[filter.q_key];
        }
        return acc;
      },
      {},
    );

    if (Object.values(newOptionFilters).length === MAX_NUM_OPTION) {
      return newOptionFilters;
    } else {
      return optionsFilter.value;
    }
  });

  const displayedCategories = computed(() => {
    if (
      !route.query.categories &&
      !route.query.sub_categories &&
      !route.query.collections
    ) {
      return {
        sub_categories: undefined,
        collections: undefined,
        brands: getBrands(),
      };
    }

    const subCategoryIds = new Set(
      route.query.sub_categories?.toString().split(",") ?? [],
    );

    const collectionIds = new Set(
      route.query.collections?.toString().split(",") ?? [],
    );

    let selectedCategory: Category | undefined;

    if (route.query.categories) {
      selectedCategory = categoriesMap.value.get(
        route.query.categories as string,
      );
    }

    if (!selectedCategory && subCategoryIds.size > 0) {
      // Get first matching subcategory's parent
      const firstSubCategoryId = subCategoryIds.values().next().value;
      const subcategoryData = subcategoriesMap.value.get(
        firstSubCategoryId ?? "",
      );
      if (subcategoryData) {
        selectedCategory = categoriesMap.value.get(subcategoryData.parentId);
      }
    }

    if (!selectedCategory && collectionIds.size > 0) {
      // Get first matching collection's parent
      const firstCollectionId = collectionIds.values().next().value;
      const collectionData = collectionsMap.value.get(firstCollectionId ?? "");
      if (collectionData) {
        selectedCategory = categoriesMap.value.get(
          collectionData.grandParentId ?? "",
        );
      }
    }

    let subCategories: Category[] = [];
    let collections: Category[] = [];

    if (selectedCategory) {
      subCategories = selectedCategory.children;

      if (subCategoryIds.size > 0) {
        collections = selectedCategory.children.reduce((acc, subCategory) => {
          if (subCategoryIds.has(subCategory.id)) {
            acc.push(...subCategory.children);
          }
          return acc;
        }, [] as Category[]);
      }
    }

    return {
      sub_categories: getSubcategories(subCategories),
      collections: getCollections(collections),
      brands: getBrands(),
    };
  });

  const isApplied = (key: string, value: string): boolean => {
    if (
      route.query[key]?.toString()?.split(",").filter(Boolean).includes(value)
    ) {
      return true;
    }

    const options = JSON.parse(route.query.options?.toString() ?? "{}");
    return options[key]?.split("|").filter(Boolean).includes(value) ?? false;
  };

  const fetchAllFilters = async (ids?: string) => {
    await fetchFilters(ids);
  };

  const applyFilter = async (
    key: string,
    value: string,
    isOption?: boolean,
  ) => {
    if (isOption) {
      // Parse current options or initialize empty object
      const currentOptions = JSON.parse(
        route.query.options?.toString() ?? "{}",
      ) as Record<string, string>;

      // Get current values for this option key
      const currentValues =
        currentOptions[key]?.split("|").filter(Boolean) ?? [];

      // Update values using the same logic as regular filters
      const newValues = currentValues.includes(value)
        ? currentValues.filter((v) => v !== value)
        : [...currentValues, value];

      // Update options object
      const newOptions = {
        ...currentOptions,
        [key]: newValues.length ? newValues.join("|") : undefined,
      };

      // Clean up undefined values
      Object.keys(newOptions).forEach((k) => {
        if (newOptions[k] === undefined) {
          delete newOptions[k];
        }
      });

      router.replace({
        path: paths.products,
        query: {
          ...route.query,
          alternatives: undefined,
          options: Object.keys(newOptions).length
            ? JSON.stringify(newOptions)
            : undefined,
        },
      });
    } else {
      const currentValues =
        route.query[key]?.toString()?.split(",").filter(Boolean) ?? [];
      if (key === "price") {
        const newValue = currentValues.join(",").includes(value)
          ? undefined
          : value;

        router.replace({
          path: paths.products,
          query: {
            ...route.query,
            alternatives: undefined,
            price: newValue,
          },
        });
      } else {
        const newValues = currentValues.includes(value)
          ? currentValues.filter((v) => v !== value)
          : [...currentValues, value];

        await router.replace({
          path: paths.products,
          query: {
            ...route.query,
            alternatives: undefined,
            [key]: newValues.length ? newValues.join(",") : undefined,
          },
        });

        if (
          key === "categories" ||
          key === "sub_categories" ||
          key === "collections"
        ) {
          const ids = [
            route.query.categories,
            route.query.sub_categories,
            route.query.collections,
          ];

          const idsOutput = ids.reduce((acc, item) => {
            if (item && acc === "") {
              return item;
            } else if (item) {
              return acc + "," + item;
            } else {
              return acc;
            }
          });

          await fetchFilters(idsOutput?.toString());

          if (
            displayedOptionsFilter.value &&
            !Object.values(displayedOptionsFilter.value).length
          ) {
            router.replace({
              path: paths.products,
              query: {
                ...route.query,
                alternatives: undefined,
                options: undefined,
              },
            });
          }
        }
      }
    }
  };

  const clearAllFilters = async () => {
    await router.replace({
      path: paths.products,
      query: {
        filter_bar: route.query.filter_bar,
        categories: route.query.categories,
      },
    });

    fetchFilters(route.query?.categories?.toString());
  };

  const fetchFilters = async (categoriesIds?: string) => {
    if (loading) loading.value = true;
    priceFilterBoundaries.value = undefined;
    optionsFilter.value = undefined;

    const { res } = await useApi<FilterResponse>(
      `/api/v3/filters?category_id=${categoriesIds}`,
    );

    if (res?.data.price)
      priceFilterBoundaries.value = {
        ...res.data.price,
        currency: nuxtApp.$currency,
      };

    optionsFilter.value = res?.data.options.reduce((acc, option) => {
      return {
        ...acc,
        [option.title]: option.option_values.map((optionValue) => ({
          q_key: option.type,
          sectionTitle: option.title,
          type: "OPTION",
          label: optionValue.title,
          value: optionValue.id.toString(),
          color: optionValue.value,
        })),
      };
    }, {});

    if (res?.data) customFilters.value = getCustomFilters(res.data);

    if (loading) loading.value = false;
  };

  const getSubcategories = (sub_categories: Category[]) => {
    if (sub_categories?.length) {
      const subCategories = sub_categories.map<BaseFilterProps>(
        (subcategory) => ({
          q_key: "sub_categories",
          sectionTitle: "sub categories",
          label: subcategory.title,
          value: subcategory.id,
        }),
      );

      return subCategories;
    }
  };

  const getCollections = (collectionsArr: Category[]) => {
    if (collectionsArr?.length) {
      const collectionsFilter = collectionsArr.map<BaseFilterProps>(
        (collection) => ({
          q_key: "collections",
          sectionTitle: "Collections",
          label: collection.title,
          value: collection.id,
        }),
      );

      return collectionsFilter;
    }
  };

  const getBrands = () => {
    if (filteredBrands.value?.length) {
      const brandsFilter = filteredBrands.value.map<BaseFilterProps>(
        (brand) => ({
          q_key: "brands",
          sectionTitle: "Brands",
          label: brand.title,
          value: brand.id,
        }),
      );

      return brandsFilter;
    }
  };

  function getCustomFilters<T extends Filter>(filter: T) {
    type CustomFilterKeys = Exclude<keyof T, "price" | "options">;

    const result = {} as Record<CustomFilters, BaseFilterProps[]>;

    // Get all keys except price and options
    const keys = Object.keys(filter).filter(
      (key) => key !== "price" && key !== "options",
    ) as CustomFilterKeys[];

    keys.forEach((key) => {
      const customFilterOptions = filter[key] as CustomFilterOption[];

      if (customFilterOptions && customFilterOptions.length) {
        result[key as CustomFilters] = customFilterOptions.map((option) => ({
          q_key: key.toString(),
          sectionTitle: key.toString().replaceAll("_", " "),
          label: option.name,
          value: option.id.toString(),
        }));
      }
    });

    return result;
  }

  return {
    optionsFilter,
    priceFilterBoundaries,
    specialTagsFilter,
    customFilters,
    appliedFilters,
    displayedOptionsFilter,
    displayedCategories,
    applyFilter,
    isApplied,
    fetchFilters,
    clearAllFilters,
    fetchAllFilters,
  };
}
